import { Browser } from "@capacitor/browser";
import { getPrivateSessionState } from "@/lib/api/private-session";
import { isIosCompanionApp, isSellerNativeAppOrigin } from "./runtime";

export type NativeMetaProvider = "FACEBOOK" | "INSTAGRAM";
export type NativeMetaPurpose = "LOGIN" | "LINK" | "BUSINESS_CONNECT";
const STORAGE_KEY = "trioty-native-meta-pending";
const SELLER_ORIGIN = "https://seller.trioty.com";
const SECRET = /^[A-Za-z0-9_-]{43}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
// Web traffic never needs native cryptography, including local HTTP previews.
let runtimeId: string | undefined;
type Pending = {
  verifier: string;
  requestId: string;
  attemptId?: string;
  provider: NativeMetaProvider;
  purpose: NativeMetaPurpose;
  businessOnly?: true;
  locale: "ar" | "en";
  expiresAt: number;
  runtimeId: string;
  generation: number;
  actorId?: string;
  storeSlug?: string;
};
export type NativeMetaCallback = {
  ticket: string | null;
  purpose: NativeMetaPurpose;
  provider: NativeMetaProvider;
  native: Pending & { attemptId: string };
  expiresAt: number;
};
type Status = "pending" | "interrupted" | "restart" | null;
let status: Status = null;
let callback: NativeMetaCallback | null = null;
const listeners = new Set<() => void>();
const emit = () => { for (const listener of listeners) listener(); };
export const subscribeNativeMeta = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};
export const getNativeMetaStatus = () => status;

function setStatus(next: Status) { status = next; emit(); }
function removePending() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* No proof can be restored. */ }
}
export function clearNativeMetaAuthorization(requestId?: string) {
  if (requestId && readNativeMetaPending()?.requestId !== requestId) return;
  removePending();
  callback = null;
  setStatus(null);
}

/** A single short-lived verifier is scoped to this WebView session. No URL,
 * provider code, completion ticket, password or session cookie is persisted. */
export function readNativeMetaPending(): Pending | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Pending;
    if (!SECRET.test(value.verifier) || !UUID.test(value.requestId) ||
      (value.attemptId !== undefined && !UUID.test(value.attemptId)) ||
      !["FACEBOOK", "INSTAGRAM"].includes(value.provider) ||
      !["LOGIN", "LINK", "BUSINESS_CONNECT"].includes(value.purpose) ||
      (isIosCompanionApp() && (value.purpose !== "BUSINESS_CONNECT" || value.businessOnly !== true)) ||
      !["ar", "en"].includes(value.locale) ||
      !Number.isFinite(value.expiresAt) || value.expiresAt <= Date.now() ||
      value.expiresAt > Date.now() + 10 * 60_000 ||
      typeof value.runtimeId !== "string" || !Number.isInteger(value.generation) ||
      (value.actorId !== undefined && typeof value.actorId !== "string") ||
      (value.storeSlug !== undefined && typeof value.storeSlug !== "string")) {
      removePending();
      return null;
    }
    return value;
  } catch { removePending(); return null; }
}

function base64url(value: Uint8Array) {
  return btoa(String.fromCharCode(...value)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
export async function prepareNativeMetaAuthorization(input: {
  provider: NativeMetaProvider; purpose: NativeMetaPurpose; locale: "ar" | "en";
}, signal?: AbortSignal): Promise<{ challenge: string; requestId: string }> {
  if (signal?.aborted) throw signal.reason ?? new DOMException("Authorization cancelled", "AbortError");
  clearNativeMetaAuthorization();
  const session = getPrivateSessionState();
  if (session.changing || !isSellerNativeAppOrigin() ||
    (isIosCompanionApp() && (input.purpose !== "BUSINESS_CONNECT" || !session.actorId || !session.storeSlug)))
    throw new Error("Native authorization unavailable");
  runtimeId ??= crypto.randomUUID();
  const requestId = crypto.randomUUID();
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
  // Storage failure must stop before issuing an OAuth attempt that cannot return.
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
    provider: input.provider, purpose: input.purpose, locale: input.locale,
    // Pre-remediation business attempts may also enroll a sign-in identity.
    ...(isIosCompanionApp() ? { businessOnly: true as const } : {}),
    verifier, requestId, expiresAt: Date.now() + 10 * 60_000, runtimeId,
    generation: session.generation, actorId: session.actorId, storeSlug: session.storeSlug,
  } satisfies Pending));
  // Cancellation can arrive after HTTP settles but before the caller opens the
  // browser. A successful handoff never aborts this signal or erases its proof.
  const abort = () => clearNativeMetaAuthorization(requestId);
  signal?.addEventListener("abort", abort, { once: true });
  let challenge: string;
  try {
    challenge = base64url(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))));
    if (signal?.aborted) throw signal.reason ?? new DOMException("Authorization cancelled", "AbortError");
  } catch (error) {
    signal?.removeEventListener("abort", abort);
    clearNativeMetaAuthorization(requestId);
    throw error;
  }
  if (getPrivateSessionState().generation !== session.generation || readNativeMetaPending()?.requestId !== requestId) {
    signal?.removeEventListener("abort", abort);
    clearNativeMetaAuthorization(requestId);
    throw new DOMException("Private session changed or authorization superseded", "AbortError");
  }
  return { challenge, requestId };
}

export function registerNativeMetaAttempt(attemptId: string, requestId: string) {
  const pending = readNativeMetaPending();
  if (!pending || pending.requestId !== requestId || !UUID.test(attemptId)) throw new Error("Invalid native authorization attempt");
  if (pending.runtimeId === runtimeId && pending.generation !== getPrivateSessionState().generation)
    throw new DOMException("Private session changed", "AbortError");
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...pending, attemptId }));
}

export function validNativeMetaLaunchUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.origin === SELLER_ORIGIN && !url.username && !url.password && !url.port &&
      !url.hash && url.pathname === "/api/public/meta/native/authorize" &&
      [...url.searchParams.keys()].length === 1 && SECRET.test(url.searchParams.get("launch") ?? "");
  } catch { return false; }
}

export async function openNativeMetaAuthorization(value: string, provider?: NativeMetaProvider, attemptId?: string) {
  const pending = readNativeMetaPending();
  try {
    if (!pending?.attemptId || pending.attemptId !== attemptId || (provider && pending.provider !== provider) || !validNativeMetaLaunchUrl(value))
      throw new Error("Invalid native authorization destination");
    setStatus("pending");
    await Browser.open({ url: value, presentationStyle: "popover" });
  } catch (error) {
    if (pending?.attemptId === attemptId) clearNativeMetaAuthorization(pending?.requestId);
    throw error;
  }
}

export function parseNativeMetaCallback(value: string) {
  try {
    const url = new URL(value);
    if (url.protocol !== "trioty-seller:" || url.hostname !== "meta" ||
      url.pathname !== "/callback" || url.username || url.password || url.port || url.hash) return null;
    const params = url.searchParams;
    if ([...params.keys()].some((key) => !["attempt", "ticket", "error", "purpose", "provider", "locale"].includes(key) || params.getAll(key).length !== 1)) return null;
    const attemptId = params.get("attempt");
    const ticket = params.get("ticket");
    const error = params.get("error");
    const purpose = params.get("purpose");
    const provider = params.get("provider");
    const locale = params.get("locale");
    if (!attemptId || !UUID.test(attemptId) ||
      !["LOGIN", "LINK", "BUSINESS_CONNECT"].includes(purpose ?? "") ||
      !["FACEBOOK", "INSTAGRAM"].includes(provider ?? "") || !["ar", "en"].includes(locale ?? "") ||
      params.has("ticket") === params.has("error") ||
      (params.has("ticket") ? !SECRET.test(ticket ?? "") : !["META_PERMISSION_DECLINED", "META_CALLBACK_FAILED"].includes(error ?? ""))) return null;
    return { attemptId, ticket, purpose, provider, locale };
  } catch { return null; }
}

/** Only a return matching a locally initiated request can reach the app's
 * confirmation page. The ticket stays in memory, never in app history. */
export function receiveNativeMetaCallback(value: string): string | null {
  const result = parseNativeMetaCallback(value);
  if (!result) return null;
  const pending = readNativeMetaPending();
  if (!pending) {
    if (!callback) setStatus("restart");
    return null;
  }
  if (pending.attemptId !== result.attemptId || pending.provider !== result.provider ||
    pending.purpose !== result.purpose || pending.locale !== result.locale) return null;
  if (pending.runtimeId === runtimeId && pending.generation !== getPrivateSessionState().generation) {
    clearNativeMetaAuthorization(); setStatus("restart"); return null;
  }
  removePending();
  runtimeId ??= crypto.randomUUID();
  callback = { ticket: result.ticket, purpose: pending.purpose, provider: pending.provider,
    native: { ...pending, attemptId: result.attemptId, runtimeId, generation: getPrivateSessionState().generation }, expiresAt: Math.min(pending.expiresAt, Date.now() + 120_000) };
  setStatus(null);
  return `/${pending.locale}/meta/callback`;
}

export function readNativeMetaCallback(): NativeMetaCallback | null {
  if (callback && (callback.expiresAt <= Date.now() ||
    (isIosCompanionApp() && (callback.purpose !== "BUSINESS_CONNECT" || callback.native.businessOnly !== true)))) callback = null;
  return callback;
}

export function finishNativeMetaCallback(attemptId: string) {
  if (callback?.native.attemptId === attemptId) callback = null;
}

export function assertNativeMetaCompletion(
  value: NativeMetaCallback,
  binding: { seller: { id: string; storeSlug: string } } | null,
) {
  const session = getPrivateSessionState();
  if (value.expiresAt <= Date.now() || session.changing ||
    (isIosCompanionApp() && (value.purpose !== "BUSINESS_CONNECT" || value.native.businessOnly !== true)) ||
    (value.native.runtimeId === runtimeId && session.generation !== value.native.generation) ||
    (binding?.seller.id ?? undefined) !== value.native.actorId ||
    (binding?.seller.storeSlug ?? undefined) !== value.native.storeSlug) {
    finishNativeMetaCallback(value.native.attemptId);
    throw new Error("Native authorization session changed or expired");
  }
}

export function markNativeMetaInterrupted() {
  if (readNativeMetaPending()) setStatus("interrupted");
  else if (status === "pending" || status === "interrupted") setStatus("restart");
}
